import React from 'react';
import { AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, Area, ResponsiveContainer } from 'recharts';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { CloseSquare } from 'iconsax-react';
import type { NodeData } from './App';
import SELECT_MAP from './constants';
import CustomSelect from './CustomSelect';


interface GraphProps {
    show: boolean,
    toggle: Function,
    node: NodeData | null,
    graphState: any[]
}
export default React.memo(function Graphs({
    show,
    toggle,
    node,
    graphState
}: GraphProps) {
    const [selectOption, setSelectOption] = React.useState<{
        value: string;
        label: string;
        disabled: boolean;
        unit: string
    }[]>([]);
    const selectOptions = React.useMemo<any>(
        () => {
            const latest = graphState.slice(-1)[0];
            const val = Object.values(SELECT_MAP).map(
                opt => ({
                    value: opt.mapKey,
                    label: opt.name,
                    disabled: Boolean(latest?.[opt.mapKey]),
                    unit: opt.unitHtml
                })
            )
            setSelectOption(val.slice(0, 4))
            return val;
        }, [graphState]
    )
    const data = React.useMemo<any>(
        () => {
            // console.log(graphState)
            if (!graphState)
                return {}
            const finalVal = {
                temp: [],
                humidity: [],
                bar_pressure: [],
                vapor_pressure: []
            } as any;
            graphState.forEach(
                (data: any) => {
                    Object.keys(data).forEach(key => {
                        if (key === 'date' || key === '_id')
                            return
                        finalVal[key].push({
                            name: formatTime(new Date(data.date).toLocaleTimeString()),
                            uv: data[key]
                        })
                    })
                }
            )
            return finalVal;

        }, [graphState]
    )

    return <div className={`fixed h-screen transition-all z-[100] duration-1000 left-0 lg:left-96 w-full lg:w-[calc(100%-24rem)] p-5 ${show ? '-top-0 opacity-100' : '-top-[100vh] opacity-0'}`}>
        <div className='lg:bg-slate-800/90 bg-slate-800 w-full h-full rounded-xl  relative'>

            <CloseSquare size="32" color="white" className='absolute top-3 left-3 cursor-pointer z-10' variant="Bold" onClick={toggle as any} />

            <Scrollbars style={{ width: '100%', height: '100%' }}>
                <div className='flex flex-col text-white justify-center items-center p-5'>
                    <h1 className='text-2xl'>{node?.name}</h1>
                    <h1>{node?.location.name}</h1>

                    <div className='mt-10'>
                        <h1 className='text-center'>Select data to show</h1>
                        <CustomSelect options={selectOptions} value={selectOption} onChange={(e) => e.length > 1 && e.length < 7 ? setSelectOption(e) : null} />
                    </div>
                    {
                        selectOption.map(
                            option => {
                                const specData = data[option.value]
                                if (!specData)
                                    return <></>
                                return (
                                    <>
                                        <div className='grid lg:grid-cols-2 xl:px-10 w-full mt-10 justify-items-center'>
                                            <div className='grid grid-cols-2 w-full gap-5'>
                                                <h1 className='text-xl font-semibold'>{option.label}</h1>
                                                <h1 className='text-xl font-semibold'>{specData.slice(-1)[0]?.uv} <span dangerouslySetInnerHTML={{ __html: option.unit }}></span></h1>
                                            </div>
                                            <div className=' mt-5 lg:mt-0'>
                                                <Scrollbars style={{ width: '100%', height: '200px' }}>
                                                    <ResponsiveContainer width={600}height={200}>
                                                        <AreaChart data={specData}
                                                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                                            <defs>
                                                                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                                                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                                                                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                                                                </linearGradient>
                                                            </defs>
                                                            <XAxis tick={{ fill: 'white' }} dataKey="name" />
                                                            <YAxis tick={{ fill: 'white' }} color='white' />
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <Tooltip />
                                                            <Area type="monotone" dataKey="uv" stroke="#8884d8" fillOpacity={1} fill="url(#colorUv)" />
                                                            {/* <Area type="monotone" dataKey="pv" stroke="#82ca9d" fillOpacity={1} fill="url(#colorPv)" /> */}
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </Scrollbars>
                                            </div>
                                        </div>
                                        <hr />
                                    </>
                                )
                            }
                        )
                    }

                </div>
            </Scrollbars>
        </div>
    </div>
})

function formatTime(time : string) {
    const amPm = time.split(" ")[1]
    return time.split(":")
               .filter((_, ind, arr) => ind !==  arr.length - 1)
               .join(":") + " " + amPm
}